import { safeStorageGet, safeStorageSet } from '@utils'

type AdStorage = 'granted' | 'denied'

const SENT_EVENT_KEY_PREFIX = 'uetSent_'

/**
 * Until `bat.js` loads, `window.uetq` is a plain array the tag replays on init;
 * afterwards it is the UET instance, which exposes the same `push`. Both accept
 * the calls below, so queueing before the tag arrives is safe — and required for
 * the consent state.
 */
const pushToUetq = (...args: unknown[]) => {
  window.uetq = window.uetq || []
  window.uetq.push(...args)
}

/**
 * Updates the consent UET runs under. The initial `denied` default is queued by
 * a Consent Initialization tag in the GTM container, which has to run before the
 * UET tag itself — otherwise `bat.js` sets its cookies before hearing about
 * consent at all.
 *
 * @see https://help.ads.microsoft.com/#apex/ads/en/60341/1
 */
export const setBingConsent = (adStorage: AdStorage) => {
  pushToUetq('consent', 'update', { ad_storage: adStorage })
}

/**
 * Sends a custom UET event (`evt=custom`), which is how conversion goals are
 * matched in Microsoft Advertising: `eventName` lands in `ea`, `event_category`
 * in `ec`, `revenue_value` in `gv` and `currency` in `gc`.
 *
 * `dedupeKey` guards against the same conversion firing twice — the checkout can
 * call into this from both the Stripe and the PayPal success paths.
 */
export const pushBingEvent = (eventName: string, data: object, dedupeKey?: string) => {
  if (!window.uetq) return

  if (dedupeKey) {
    const storageKey = `${SENT_EVENT_KEY_PREFIX}${eventName}_${dedupeKey}`
    if (safeStorageGet(storageKey, 'local')) return
    safeStorageSet(storageKey, 'true', 'local')
  }

  pushToUetq('event', eventName, data)
}
